import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ClipboardCheck, AlertTriangle, ArrowRightLeft } from "lucide-react";

const EMPTY = { entity_type: "tank", entity_id: "", physical_quantity: 0, supervisor: "", variance_reason: "measurement", remarks: "" };

export default function StockAudit() {
  const qc = useQueryClient();
  const audits = useQuery({ queryKey: ["stock-audits"], queryFn: () => api.get("/inventory/audit").then((r) => r.data) });
  const tanks = useQuery({ queryKey: ["audit-tanks"], queryFn: () => api.get("/inventory/tanks").then((r) => r.data) }).data?.items || [];
  const bowsers = useQuery({ queryKey: ["audit-bowsers"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) }).data?.items || [];

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const entities = form.entity_type === "tank" ? tanks : bowsers;
  const selectedEntity = entities.find((e) => e.id === form.entity_id);
  const systemQty = selectedEntity?.current_stock || 0;
  const variance = Number((Number(form.physical_quantity) - systemQty).toFixed(2));
  const variancePct = systemQty ? Number((variance / systemQty * 100).toFixed(2)) : 0;

  const create = useMutation({
    mutationFn: () => api.post("/inventory/audit", form),
    onSuccess: () => { toast.success("Audit recorded"); qc.invalidateQueries({ queryKey: ["stock-audits"] }); setOpen(false); setForm(EMPTY); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const reconcile = useMutation({
    mutationFn: (id) => api.post(`/inventory/audit/${id}/reconcile`),
    onSuccess: (r) => { toast.success(`Stock adjusted to ${r.data.new_stock.toFixed(2)}L`); qc.invalidateQueries({ queryKey: ["stock-audits"] }); qc.invalidateQueries({ queryKey: ["audit-tanks"] }); qc.invalidateQueries({ queryKey: ["audit-bowsers"] }); },
  });

  const items = audits.data?.items || [];

  return (
    <div className="space-y-4" data-testid="stock-audit-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Physical Stock Verification</h1>
        <p className="text-sm text-muted-foreground">Record daily dip-stick measurements against system stock. Reconcile confirmed variances after root-cause analysis.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi icon={ClipboardCheck} label="Total audits" value={audits.data?.count || 0} />
        <Kpi icon={AlertTriangle} label="Over 1% variance" value={audits.data?.over_threshold_count || 0} />
        <Kpi icon={ArrowRightLeft} label="Net variance (L)" value={(audits.data?.total_variance || 0).toFixed(2)} />
      </div>
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Audit history</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="btn-new-audit"><Plus size={14} className="mr-1" /> Record measurement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Physical stock measurement</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Entity type</Label>
                  <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v, entity_id: "" })}>
                    <SelectTrigger data-testid="audit-entity-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tank">Storage tank</SelectItem>
                      <SelectItem value="bowser">Bowser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{form.entity_type === "tank" ? "Tank" : "Bowser"} <span className="text-status-cancelled">*</span></Label>
                  <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })}>
                    <SelectTrigger data-testid="audit-entity"><SelectValue placeholder={`Select ${form.entity_type}`} /></SelectTrigger>
                    <SelectContent>
                      {entities.map((e) => (<SelectItem key={e.id} value={e.id}>{e.code} — {e.current_stock?.toFixed(0) || 0}L in system</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>System stock (L)</Label><Input value={systemQty.toFixed(2)} disabled /></div>
                <div><Label>Physical measurement (L) <span className="text-status-cancelled">*</span></Label><Input type="number" step="0.01" value={form.physical_quantity || ""} onChange={(e) => setForm({ ...form, physical_quantity: Number(e.target.value) })} data-testid="audit-physical" /></div>

                <div className={`col-span-2 rounded-md border-2 p-3 ${Math.abs(variancePct) > 1 ? "border-status-cancelled/50 bg-status-cancelled/5" : "border-border/60"}`}>
                  <div className="text-xs text-muted-foreground">Variance</div>
                  <div className="mt-1 flex items-baseline gap-3">
                    <span className={`font-display text-2xl font-black ${Math.abs(variancePct) > 1 ? "text-status-cancelled" : "text-status-delivered"}`} data-testid="audit-variance">{variance > 0 ? "+" : ""}{variance} L</span>
                    <span className="text-sm text-muted-foreground">({variancePct > 0 ? "+" : ""}{variancePct}%)</span>
                  </div>
                </div>

                <div>
                  <Label>Variance reason</Label>
                  <Select value={form.variance_reason} onValueChange={(v) => setForm({ ...form, variance_reason: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spillage">Spillage</SelectItem>
                      <SelectItem value="evaporation">Evaporation</SelectItem>
                      <SelectItem value="pilferage">Pilferage</SelectItem>
                      <SelectItem value="measurement">Measurement error</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Supervisor</Label><Input value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })} placeholder="Witness name" /></div>
                <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={() => create.mutate()} disabled={!form.entity_id || form.physical_quantity < 0 || create.isPending} data-testid="btn-save-audit">
                  {create.isPending ? "Saving…" : "Record audit"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Code</th><th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Entity</th>
                <th className="px-4 py-2 text-right">System</th>
                <th className="px-4 py-2 text-right">Physical</th>
                <th className="px-4 py-2 text-right">Variance</th>
                <th className="px-4 py-2">Reason</th>
                <th className="px-4 py-2">Verified by</th>
                <th className="px-4 py-2 text-right">Reconcile</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-border/40 text-sm" data-testid={`audit-row-${a.code}`}>
                  <td className="px-4 py-2 font-mono">{a.code}</td>
                  <td className="px-4 py-2 text-xs">{a.date?.slice(0, 10)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.entity_type}/{a.entity_code}</td>
                  <td className="px-4 py-2 text-right font-mono">{a.system_quantity.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right font-mono">{a.physical_quantity.toFixed(2)}</td>
                  <td className={`px-4 py-2 text-right font-mono font-semibold ${Math.abs(a.variance_percent) > 1 ? "text-status-cancelled" : "text-status-delivered"}`}>
                    {a.variance > 0 ? "+" : ""}{a.variance.toFixed(2)} ({a.variance_percent}%)
                  </td>
                  <td className="px-4 py-2 text-xs capitalize">{a.variance_reason}</td>
                  <td className="px-4 py-2 text-xs">{a.verified_by}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => reconcile.mutate(a.id)} data-testid={`audit-reconcile-${a.code}`}>
                      <ArrowRightLeft size={12} className="mr-1" /> Adjust stock
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  <ClipboardCheck size={32} className="mx-auto mb-2 opacity-40" />
                  No audits yet. Record your first physical measurement.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={14} />
    </div>
    <div className="mt-2 font-display text-xl font-black">{value}</div>
  </div>
);
